import React, { SyntheticEvent, useCallback } from 'react'

import styled from '@emotion/styled'
import { cssRecipeTile, cssRecipeTileContainer } from './styles'
import { SwapAlternativeOptions } from '../SwapAlternativeOptions/SwapAlternativeOptions'
import {
  CollectionUUID,
  MenuApiPreferencesResponse,
  Recipe,
  RecipeCoreId,
} from '@library/menu-service'
import { AlternativeOptions } from '../../model/alternativeOptions/alternativeOptions'
import { RecipeTileHeader } from '../RecipeTileHeader'

const OuterContainer = styled.div(cssRecipeTile)

const TileContainer = styled.div(() => ({
  ...cssRecipeTileContainer,
}))

export type RecipeTileProps = {
  recipe: Recipe
  currentCollectionId: CollectionUUID
  menuId: string | null
  onClick: (recipeId: string) => void
  menuPreferences?: MenuApiPreferencesResponse
  setBrowserCTAVisible: () => void
  canAddRecipes: boolean
  addRecipe: (recipeId: string) => void
  removeRecipe: (recipeId: string) => void
  reachedLimit: boolean
  isInBasket: boolean
  isOutOfStock: boolean
  selectVariant: ({
    variant,
    variantOutOfStock,
    originalRecipeId,
    recipeReference,
  }: {
    variant: Recipe
    variantOutOfStock: boolean
    originalRecipeId: string
    recipeReference: string
  }) => void
  alternativeOptions: AlternativeOptions
  onOpenAlternativeOptions?: () => void
  isLoadingOptions?: boolean
  recipeReference: string
  originalId: RecipeCoreId
  isProteinOnSignupEnabled?: boolean
  lazy?: boolean
  insideOverflow?: boolean
  index?: number
  quantity?: number
  showCustomisationEducation?: boolean
  closeCustomisationEducation?: () => void
  isScrollableBasketEnabled: boolean
}

export const RecipeTile = ({
  recipe,
  currentCollectionId: categoryId,
  menuId,
  onClick,
  menuPreferences,
  setBrowserCTAVisible,
  canAddRecipes,
  addRecipe,
  removeRecipe,
  reachedLimit,
  isInBasket,
  isOutOfStock,
  selectVariant,
  alternativeOptions,
  isLoadingOptions,
  onOpenAlternativeOptions,
  recipeReference,
  originalId,
  isProteinOnSignupEnabled,
  lazy = true,
  insideOverflow = false,
  index,
  quantity = 0,
  showCustomisationEducation = false,
  closeCustomisationEducation = undefined,
  isScrollableBasketEnabled,
}: RecipeTileProps) => {
  const { coreId: recipeCoreId, id: recipeUuid } = recipe

  const selectRecipeVariant = useCallback(
    ({ variant, variantOutOfStock }: { variant: Recipe; variantOutOfStock: boolean }) => {
      selectVariant({
        variant,
        variantOutOfStock,
        originalRecipeId: originalId,
        recipeReference,
      })
    },
    [originalId, recipeReference, selectVariant],
  )

  // should never happen but caters for loading state
  if (categoryId === null) {
    return null
  }

  const handleOnClick = (e: SyntheticEvent) => {
    e.stopPropagation()
    onClick(recipeUuid)
  }

  return (
    <OuterContainer
      role="button"
      tabIndex={0}
      data-testing={isOutOfStock ? 'menuRecipeOutOfStock' : 'menuRecipeViewDetails'}
      data-testing-id={recipeCoreId}
      onClick={handleOnClick}
      onKeyPress={handleOnClick}
    >
      <TileContainer>
        <RecipeTileHeader
          recipe={recipe}
          currentCollectionId={categoryId}
          menuPreferences={menuPreferences}
          setBrowserCTAVisible={setBrowserCTAVisible}
          canAddRecipes={canAddRecipes}
          addRecipe={addRecipe}
          removeRecipe={removeRecipe}
          reachedLimit={reachedLimit}
          isInBasket={isInBasket}
          isOutOfStock={isOutOfStock}
          lazy={lazy}
          insideOverflow={insideOverflow}
          isProteinOnSignupEnabled={isProteinOnSignupEnabled}
          index={index}
          showPurchaseInfo={true}
          isScrollableBasketEnabled={isScrollableBasketEnabled}
        />
        <SwapAlternativeOptions
          currentRecipeCoreId={recipeCoreId}
          currentCollectionId={categoryId}
          menuId={menuId}
          menuPreferences={menuPreferences}
          recipe={recipe}
          originalId={originalId}
          reachedLimit={reachedLimit}
          selectVariant={selectRecipeVariant}
          addRecipeToBasket={addRecipe}
          alternativeOptions={alternativeOptions}
          onOpenAlternativeOptions={onOpenAlternativeOptions}
          isLoading={isLoadingOptions || false}
          canAddRecipes={canAddRecipes}
          removeRecipe={removeRecipe}
          quantity={quantity}
          recipeIndex={index}
          showCustomisationEducation={showCustomisationEducation}
          closeCustomisationEducation={closeCustomisationEducation}
          isScrollableBasketEnabled={isScrollableBasketEnabled}
        />
      </TileContainer>
    </OuterContainer>
  )
}
